import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {humanizeBytes, UploaderOptions, UploadFile, UploadInput, UploadOutput} from 'ngx-uploader';
import * as _ from 'underscore';
import { SystemGlobalsService } from '../system-globals.service';

interface FilenameRecord {
  uid: string;
  name: string;
}

@Component({
  selector: 'file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss']
})

export class FileUploadComponent implements OnInit, OnChanges {
  options: UploaderOptions;
  formData: FormData;
  _files: UploadFile[];
  uploadInput: EventEmitter<UploadInput>;
  humanizeBytes: Function;
  dragOver: boolean;
  showDropZone: boolean = true;
  mediaLibraryTypesAllowed: string[];
  referenceLibraryTypesAllowed: string[];
  acceptedFileTypes: string = '';
  uploadedFiles: UploadFile[] = [];
  showPreloader: boolean = false;

  @Input() showFileList: true;
  @Input() isFullHeight: boolean = false
  @Input() maxFiles: number = 5;
  @Input() uploadUrl: string = '/files/uploadFile';
  @Input() extraUrlParams: string = '';
  @Input() paramName: string = 'file';
  @Input() files: string = '';
  @Input() maxImageHeight: string = '4rem';
  @Input() hideOutputContainer: boolean = false;
  @Input() label: string = '';
  @Input() title: string = '';
  @Input() extractOnUpload: boolean;
  @Input() custom: string = '';
  @Input() folderUid: string = '';
  @Input() forceShowImage: boolean = false;

  @Output() filesChange = new EventEmitter();
  @Output() currentUploadedFile = new EventEmitter();
  @Output() fileUploadInProcess = new EventEmitter();

  constructor(
    private globals: SystemGlobalsService,
    private http: HttpClient,
  ) {
    this._files = []; // local uploading files array
    this.uploadedFiles = [];
    this.uploadInput = new EventEmitter<UploadInput>(); // input events, we use this to emit data to ngx-uploader
    this.humanizeBytes = humanizeBytes;

    if (globals.constants.uploadUrl) {
      this.uploadUrl = globals.constants.uploadUrl + '?nkey=' + globals.constants.uploadNetworkKey;
    }
  }

  ngOnInit() {
    this.onWriteValue(this.files);
  }

  /**
   * Parents often bind [files] after async loads (e.g. OLTE edit detail). Without this,
   * the control only ever shows whatever was present at init — usually empty.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['files'] && !changes['files'].firstChange) {
      this.onWriteValue(changes['files'].currentValue);
    }
  }

  onWriteValue(files) {
    if (!files || !String(files).trim()) {
      this.uploadedFiles = [];
      return;
    }

    const imgTypes = ['gif', 'jpg', 'png', 'jpeg'];
    let fileIndex = 0;

    this.uploadedFiles = String(files).split(',').filter(file => !!file.trim()).map(function (file) {
      fileIndex += 1;
      file = file.trim();
      // Fresh regex each iteration — /g lastIndex would skip matches across files.
      const nameMatch = /.*\/(.*)/.exec(file);
      let name = nameMatch ? nameMatch[1] : file;

      file = file.replace('/files/', '');

      const extMatch = /(?:\.([^.]+))?$/.exec(file.toLowerCase());
      const ext = extMatch && extMatch[1] ? extMatch[1] : '';

      return {
        id: _.uniqueId('file-'),
        fileIndex: fileIndex,
        lastModifiedDate: new Date(),
        name: '',
        size: 0,
        type: (imgTypes.indexOf(ext) != -1 ? 'image' : 'file'),
        form: null,
        progress: null,
        response: {
          uid: file,
          name: name
        },
      }
    });

    // Paths store S3/hash UIDs — resolve to the original display names like legacy KO.
    this.resolveFilenames(this.uploadedFiles.map(file => file.response.uid));
  }

  private resolveFilenames(uids: string[]): void {
    const cleanUids = uids.map(uid => String(uid || '').trim()).filter(uid => !!uid);
    if (!cleanUids.length) {
      return;
    }

    this.http.post<any>('/files/getFilenames', { uids: cleanUids }).subscribe({
      next: (response) => {
        const records: FilenameRecord[] = Array.isArray(response)
          ? response
          : (Array.isArray(response?.data) ? response.data : []);

        if (!records.length) {
          return;
        }

        const byUid: { [uid: string]: string } = {};
        records.forEach(record => {
          const uid = record && (record.uid || (record as any).UID);
          const name = record && record.name;
          if (uid && name) {
            byUid[String(uid)] = String(name);
          }
        });

        this.uploadedFiles.forEach(file => {
          const resolved = byUid[file.response.uid];
          if (resolved) {
            file.response.name = resolved;
          }
        });
      },
      error: (error) => {
        console.error('Error resolving file names:', error);
      },
    });
  }

  onChange(): void {
    const filesJoined = this.uploadedFiles
      .map((file) => {
        const uid = file && file.response && file.response.uid ? String(file.response.uid).trim() : '';
        return uid ? '/files/' + uid : '';
      })
      .filter((path) => !!path)
      .join(',');

    this.currentUploadedFile.emit(this.uploadedFiles);
    setTimeout(() => this.filesChange.emit(filesJoined), 0);
  }

  getUploadUrl(): string {
    let params = this.extraUrlParams || '';
    if (this.extractOnUpload) {
      params += '&_extractOnUpload=true';
    }
    if (this.custom != null && this.custom.length) {
      params += '&_custom=' + this.custom;
    }

    return this.uploadUrl + '&paramName=' + this.paramName + '&' + params;
  }

  onUploadOutput(output: UploadOutput): void {

    switch (output.type) {
      case 'allAddedToQueue':
        const event: UploadInput = {
          type: 'uploadAll',
          url: this.getUploadUrl(),
          method: 'POST',
          data: {},
        };

        this.uploadInput.emit(event);
        break;

      case 'addedToQueue':
        this.showPreloader = true;
        this.fileUploadInProcess.emit(output.file)
        //if (output.file !== undefined) this.files.push(output.file);
        break;

      case 'uploading':
        if (output.file !== undefined) {
          const index = this._files.findIndex(file => typeof output.file !== 'undefined' && file.id === output.file.id);
          this._files[index] = output.file;
        }
        break;

      case 'done':
        this.showPreloader = false;
        if (output.file && output.file.response && output.file.response.uid) {
          this.uploadedFiles.push(output.file);
          this.onChange();
        }
        break;

      case 'removed':
        this._files = this._files.filter((file: UploadFile) => file !== output.file);
        break;

      case 'dragOver':
        this.dragOver = true;
        break;

      case 'dragOut':
        this.dragOver = false;
        break;

      case 'drop':
        this.dragOver = false;
        break;
    }

  }

  removeUploadedFile(file: UploadFile): void {
    const index = this.uploadedFiles.findIndex(_file => _file === file);
    this.uploadedFiles.splice(index, 1);
    this.onChange();
  }

  startUpload(): void {

    const event: UploadInput = {
      type: 'uploadAll',
      url: this.getUploadUrl(),
      method: 'POST',
      data: {}
    };
    this.uploadInput.emit(event);
  }

  cancelUpload(id: string): void {
    this.uploadInput.emit({type: 'cancel', id: id});
  }

  removeFile(id: string): void {
    this.uploadInput.emit({type: 'remove', id: id});
  }

  removeAllFiles(): void {
    this.uploadInput.emit({type: 'removeAll'});
  }

}
